import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { getTagData } from '../api'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../utils/pageState'
import { TransactionsTable } from '../components/txDisplay'
import type { Transaction } from '../types'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler)

const money = (v: number) => '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const PALETTE = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#f59e0b','#10b981','#06b6d4','#3b82f6','#84cc16']

function hashColor(name: string | null | undefined, explicit?: string | null): string {
  if (explicit) return explicit
  if (!name) return '#94a3b8'
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return PALETTE[h % PALETTE.length]
}

function hasTag(t: Transaction, tagName: string): boolean {
  if (t.tag?.split(',').map(s => s.trim()).includes(tagName)) return true
  return t.splitLines?.some(l => l.tags?.includes(tagName)) ?? false
}

// Returns only the amount attributable to the given tag.
// For split transactions, sums only the lines that carry that tag.
function tagAttributedAmount(t: Transaction, tagName: string): number {
  if (t.isSplit && t.splitLines && t.splitLines.length > 0) {
    return t.splitLines
      .filter(l => l.tags?.includes(tagName))
      .reduce((s, l) => s + Math.abs(l.amount), 0)
  }
  return Math.abs(t.charges ?? 0)
}

type Period   = 'last12' | 'all' | number
type MonthSel = { year: number; month: number } | null
type TxSortF  = 'date' | 'merchant' | 'details' | 'category' | 'tag' | 'card' | 'charges'
type SortDir  = 'asc' | 'desc'
interface MonthPill { year: number; month: number; label: string }

interface TagPageState {
  period: Period; selectedMonth: MonthSel
  selectedTag: string | null
  selectedCategory: string | null; selectedMerchant: string | null
  txSort: TxSortF; txSortDir: SortDir
}

const todayGlobal = new Date()

function computeMonthPills(period: Period): MonthPill[] {
  if (period === 'all') return []
  if (period === 'last12') {
    const pills: MonthPill[] = []
    for (let i = 12; i >= 0; i--) {
      const d  = new Date(todayGlobal.getFullYear(), todayGlobal.getMonth() - i, 1)
      const yr = d.getFullYear()
      const mo = d.getMonth() + 1
      pills.push({ year: yr, month: mo, label: MONTH_NAMES[mo - 1] + " '" + String(yr).slice(2) })
    }
    return pills
  }
  const year     = period as number
  const maxMonth = year === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12
  return Array.from({ length: maxMonth }, (_, i) => ({ year, month: i + 1, label: MONTH_NAMES[i] }))
}

function buildChartMonths(refYear: number, refMonth: number, offset = 0): string[] {
  const months: string[] = []
  for (let i = 11; i >= 0; i--) {
    const d = new Date(refYear, refMonth - 1 - i + offset, 1)
    months.push(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'))
  }
  return months
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHART_OPTS: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b', padding: 10, cornerRadius: 10,
      callbacks: { label: (ctx: { raw: unknown }) => money(ctx.raw as number) },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
    y: {
      beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' }, border: { display: false },
      ticks: { color: '#94a3b8', font: { size: 10 }, callback: (v: unknown) => '$' + ((v as number) >= 1000 ? ((v as number) / 1000).toFixed(0) + 'k' : v) },
    },
  },
}

export default function Tags() {
  const navigate      = useNavigate()
  const { mode } = useMode()
  const { selectedBusinessId } = useBusinessContext()
  const isPersonal = mode === 'personal'
  const bizId      = mode === 'business' ? selectedBusinessId : null
  const autoSelectRef = useRef(true)

  const [period,           setPeriod]           = useState<Period>('last12')
  const [selectedMonth,    setSelectedMonth]    = useState<MonthSel>({ year: todayGlobal.getFullYear(), month: todayGlobal.getMonth() + 1 })
  const [selectedTag,      setSelectedTag]      = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null)
  const [chartOffset,      setChartOffset]      = useState(0)
  const [txSort,           setTxSort]           = useState<TxSortF>('date')
  const [txSortDir,        setTxSortDir]        = useState<SortDir>('desc')
  const [pendingScroll,    setPendingScroll]    = useState<number | null>(null)

  // Back-nav restore
  useEffect(() => {
    const snap = popPageSnapshot<TagPageState>('tags')
    if (!snap) return
    const s = snap.state
    setPeriod(s.period); setSelectedMonth(s.selectedMonth)
    setSelectedTag(s.selectedTag)
    setSelectedCategory(s.selectedCategory ?? null); setSelectedMerchant(s.selectedMerchant ?? null)
    setTxSort(s.txSort); setTxSortDir(s.txSortDir)
    setPendingScroll(snap.scroll)
    autoSelectRef.current = false
  }, [])

  const monthPills  = useMemo(() => computeMonthPills(period), [period])
  const isAllPeriod = period === 'all'
  const apiYear     = isAllPeriod ? undefined : period === 'last12' ? selectedMonth?.year : (period as number)
  const apiMonth    = selectedMonth?.month

  const { data: mainData, isLoading, error } = useQuery({
    queryKey: isAllPeriod ? ['tags', 'all'] : ['tags', apiYear, apiMonth],
    queryFn:  () => getTagData(apiYear, apiMonth, isAllPeriod, isPersonal, bizId),
  })

  // All-time data for chart range
  const { data: allTimeData } = useQuery({
    queryKey: ['tags', 'all'],
    queryFn:  () => getTagData(undefined, undefined, true, isPersonal, bizId),
    staleTime: 5 * 60_000,
  })

  // Chart year queries (3 years back for full nav range)
  const chartRefYearNum = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const { data: chartYearData } = useQuery({
    queryKey: ['tags', chartRefYearNum, undefined],
    queryFn:  () => getTagData(chartRefYearNum, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })
  const { data: chartPriorYearData } = useQuery({
    queryKey: ['tags', chartRefYearNum - 1, undefined],
    queryFn:  () => getTagData(chartRefYearNum - 1, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })
  const { data: chartPrior2YearData } = useQuery({
    queryKey: ['tags', chartRefYearNum - 2, undefined],
    queryFn:  () => getTagData(chartRefYearNum - 2, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })

  const allTxns     = (mainData?.transactions    ?? []) as Transaction[]
  const allTimeTxns = (allTimeData?.transactions ?? []) as Transaction[]

  // Chart reference point
  const chartRefYear  = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const chartRefMonth = selectedMonth?.month
    ?? (typeof period === 'number'
      ? ((period as number) === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12)
      : todayGlobal.getMonth() + 1)

  // Tag list: only tags with spend in the selected period
  const apiTags      = mainData?.tags ?? []
  const activeTags   = apiTags.filter(t => t.totalSpent > 0)
  const filteredList = activeTags

  // Auto-select first tag from filtered list whenever it changes
  useEffect(() => {
    if (!autoSelectRef.current || filteredList.length === 0) return
    setSelectedTag(filteredList[0].name)
    setSelectedCategory(null)
    setSelectedMerchant(null)
    setChartOffset(0)
    autoSelectRef.current = false
  }, [filteredList])

  // Chart source (multi-year for month-selected view)
  const chartSource = useMemo<Transaction[]>(() => {
    if (selectedMonth != null) {
      return [
        ...((chartYearData?.transactions    ?? []) as Transaction[]),
        ...((chartPriorYearData?.transactions  ?? []) as Transaction[]),
        ...((chartPrior2YearData?.transactions ?? []) as Transaction[]),
      ]
    }
    return allTxns
  }, [selectedMonth, chartYearData, chartPriorYearData, chartPrior2YearData, allTxns])

  // Chart data for the selected tag
  const chartData = useMemo(() => {
    if (!selectedTag) return null
    let txns = chartSource.filter(t => hasTag(t, selectedTag))
    if (selectedCategory) txns = txns.filter(t =>
      t.category === selectedCategory ||
      t.splitLines?.some(l => l.category === selectedCategory)
    )
    if (selectedMerchant) txns = txns.filter(t => t.merchantDisplayName === selectedMerchant)
    const months = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
    const map = new Map<string, number>()
    for (const t of txns) {
      if (!t.date) continue
      const ym = t.date.slice(0, 7)
      map.set(ym, (map.get(ym) ?? 0) + tagAttributedAmount(t, selectedTag))
    }
    const tagObj = apiTags.find(t => t.name === selectedTag)
    const color  = hashColor(selectedTag, tagObj?.color)
    return {
      labels: months.map(ym => { const [y, m] = ym.split('-'); return MONTH_NAMES[parseInt(m, 10) - 1] + " '" + String(y).slice(2) }),
      datasets: [{ label: selectedTag, data: months.map(ym => map.get(ym) ?? 0), borderColor: color, backgroundColor: color + '22', fill: true, tension: 0.4, pointRadius: 3, pointHoverRadius: 6, borderWidth: 2, pointBackgroundColor: color }],
    }
  }, [chartSource, selectedTag, selectedCategory, selectedMerchant, chartRefYear, chartRefMonth, chartOffset, apiTags])

  // Chart nav range
  const chartDataRange = useMemo(() => {
    if (!selectedTag) return null
    const source = [...allTimeTxns, ...chartSource, ...allTxns]
    const months = source.filter(t => hasTag(t, selectedTag) && t.date).map(t => t.date!.slice(0, 7))
    if (months.length === 0) return null
    return { min: months.reduce((a, b) => a < b ? a : b), max: months.reduce((a, b) => a > b ? a : b) }
  }, [allTimeTxns, chartSource, allTxns, selectedTag])

  const chartActiveMonths = useMemo(() => {
    if (!selectedTag || !chartData) return 0
    return chartData.datasets[0].data.filter((v: number) => v > 0).length
  }, [selectedTag, chartData])

  const tagFirstLast = useMemo(() => {
    if (!selectedTag) return null
    const dates = allTimeTxns.filter(t => hasTag(t, selectedTag) && t.date).map(t => t.date!)
    if (dates.length === 0) return null
    return { first: dates.reduce((a, b) => a < b ? a : b), last: dates.reduce((a, b) => a > b ? a : b) }
  }, [allTimeTxns, selectedTag])

  // Category breakdown for selected tag
  const tagCategories = useMemo(() => {
    if (!selectedTag) return []
    const txns = allTxns.filter(t => hasTag(t, selectedTag))
    const total = txns.reduce((s, t) => s + tagAttributedAmount(t, selectedTag), 0)
    const map = new Map<string, { spent: number; count: number }>()
    for (const t of txns) {
      if (t.isSplit && t.splitLines && t.splitLines.length > 0) {
        // For split transactions, attribute each matching line to its own category.
        for (const line of t.splitLines) {
          if (!line.tags?.includes(selectedTag) || !line.category) continue
          const ex = map.get(line.category) ?? { spent: 0, count: 0 }
          map.set(line.category, { spent: ex.spent + Math.abs(line.amount), count: ex.count + 1 })
        }
      } else if (t.category) {
        const ex = map.get(t.category) ?? { spent: 0, count: 0 }
        map.set(t.category, { spent: ex.spent + Math.abs(t.charges ?? 0), count: ex.count + 1 })
      }
    }
    return Array.from(map.entries())
      .map(([name, s]) => ({ name, totalSpent: s.spent, count: s.count, percentage: total > 0 ? (s.spent / total) * 100 : 0 }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns, selectedTag])

  // Merchant breakdown for selected tag (optionally filtered by category)
  const tagMerchants = useMemo(() => {
    if (!selectedTag) return []
    let txns = allTxns.filter(t => hasTag(t, selectedTag) && t.merchantDisplayName)
    if (selectedCategory) {
      txns = txns.filter(t =>
        t.category === selectedCategory ||
        t.splitLines?.some(l => l.tags?.includes(selectedTag) && l.category === selectedCategory)
      )
    }
    const total = txns.reduce((s, t) => s + tagAttributedAmount(t, selectedTag), 0)
    const map = new Map<string, { spent: number; count: number }>()
    for (const t of txns) {
      if (!t.merchantDisplayName) continue
      const ex = map.get(t.merchantDisplayName) ?? { spent: 0, count: 0 }
      map.set(t.merchantDisplayName, { spent: ex.spent + tagAttributedAmount(t, selectedTag), count: ex.count + 1 })
    }
    return Array.from(map.entries())
      .map(([name, s]) => ({ name, totalSpent: s.spent, count: s.count, percentage: total > 0 ? (s.spent / total) * 100 : 0 }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns, selectedTag, selectedCategory])

  // Sorted + filtered transaction list (tagged txns only)
  const sortedTxns = useMemo(() => {
    let txns = allTxns.filter(t =>
      (t.tag && t.tag.trim() !== '') ||
      (t.splitLines?.some(l => l.tags && l.tags.length > 0) ?? false)
    )
    if (selectedTag      != null) txns = txns.filter(t => hasTag(t, selectedTag))
    if (selectedCategory != null) txns = txns.filter(t =>
      t.category === selectedCategory ||
      t.splitLines?.some(l => l.category === selectedCategory)
    )
    if (selectedMerchant != null) txns = txns.filter(t => t.merchantDisplayName === selectedMerchant)
    return txns.sort((a, b) => {
      let av: string | number = '', bv: string | number = ''
      if      (txSort === 'date')     { av = a.date ?? '';                bv = b.date ?? '' }
      else if (txSort === 'merchant') { av = a.merchantDisplayName ?? ''; bv = b.merchantDisplayName ?? '' }
      else if (txSort === 'details')  { av = a.description ?? '';         bv = b.description ?? '' }
      else if (txSort === 'category') { av = a.category ?? ''; bv = b.category ?? '' }
      else if (txSort === 'tag')      { av = a.tag ?? '';       bv = b.tag ?? '' }
      else if (txSort === 'card')     { av = a.account ?? '';  bv = b.account ?? '' }
      else if (txSort === 'charges')  { av = a.charges ?? 0;   bv = b.charges ?? 0 }
      if (typeof av === 'string') { const c = av.localeCompare(bv as string); return txSortDir === 'asc' ? c : -c }
      return txSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [allTxns, selectedTag, selectedCategory, selectedMerchant, txSort, txSortDir])


  const goToEdit = (id: string) => {
    snapshotPage<TagPageState>('tags', { period, selectedMonth, selectedTag, selectedCategory, selectedMerchant, txSort, txSortDir })
    navigate('/transactions/' + id + '/edit')
  }

  // Period/month change handlers
  const changePeriod = (p: Period) => {
    autoSelectRef.current = true; setChartOffset(0)
    setPeriod(p); setSelectedMonth(null); setSelectedTag(null)
    setSelectedCategory(null); setSelectedMerchant(null)
  }
  const changeMonth = (m: MonthSel) => {
    autoSelectRef.current = true; setChartOffset(0)
    setSelectedMonth(m); setSelectedTag(null)
    setSelectedCategory(null); setSelectedMerchant(null)
  }
  const selectTag = (name: string) => {
    setChartOffset(0)
    setSelectedTag(prev => {
      if (prev === name) return null
      setSelectedCategory(null); setSelectedMerchant(null)
      return name
    })
  }

  if (isLoading) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
  if (error || !mainData) return <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load tags.</div>

  if (pendingScroll != null && !isLoading) { restoreScroll(pendingScroll); setPendingScroll(null) }

  const thisYear = new Date().getFullYear()
  const earliest = mainData.earliestYear ?? thisYear
  const minYear  = Math.max(earliest, thisYear - 5)
  const yearRange: number[] = []
  for (let y = thisYear; y >= minYear; y--) yearRange.push(y)
  const periodBtns: { label: string; value: Period }[] = [
    { label: 'Last 12 Mo', value: 'last12' },
    ...yearRange.map(y => ({ label: String(y), value: y as Period })),
    { label: 'All', value: 'all' },
  ]

  // KPIs (based on active tags in the period)
  const totalSpent    = activeTags.reduce((s, t) => s + t.totalSpent, 0)
  const tagCount      = activeTags.length

  const detailTag  = selectedTag ? (apiTags.find(t => t.name === selectedTag) ?? null) : null
  const tagColor   = hashColor(selectedTag, detailTag?.color)

  const chartRefLabel     = MONTH_NAMES[chartRefMonth - 1] + " '" + String(chartRefYear).slice(2)
  const chartWindowMonths = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
  const canGoBack    = chartDataRange != null && chartWindowMonths[0]  > chartDataRange.min
  const canGoForward = chartDataRange != null && chartWindowMonths[11] < chartDataRange.max

  const hasFilters = selectedTag != null || selectedCategory != null || selectedMerchant != null

  return (
    <div>
      <h1 className="pb-page-title mb-3">Tags</h1>

      {/* Period selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {periodBtns.map(b => (
            <button key={String(b.value)} className={'pb-chart-sel-btn' + (period === b.value ? ' active' : '')} onClick={() => changePeriod(b.value)}>{b.label}</button>
          ))}
        </div>
      </div>

      {/* Month pills */}
      {monthPills.length > 0 && (
        <div className="pb-month-pills mb-4">
          <button className={'pb-month-pill' + (selectedMonth === null ? ' active' : '')} onClick={() => changeMonth(null)}>All</button>
          {monthPills.map(p => {
            const isActive = selectedMonth?.year === p.year && selectedMonth?.month === p.month
            return <button key={p.year + '-' + p.month} className={'pb-month-pill' + (isActive ? ' active' : '')} onClick={() => changeMonth({ year: p.year, month: p.month })}>{p.label}</button>
          })}
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-up-circle me-1" />Total Spent</div><div className="pb-kpi-value" style={{ color: 'var(--pb-danger)' }}>{money(totalSpent)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-down-circle me-1" />Total Earned</div><div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(mainData.totalEarned)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-bookmark me-1" />Tags</div><div className="pb-kpi-value">{tagCount}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-receipt me-1" />Transactions</div><div className="pb-kpi-value">{mainData.totalTransactions.toLocaleString()}</div></div></div>
      </div>

      {/* Two-column layout */}
      <div className="row g-4 mb-4">

        {/* ── Left: Tag list ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ padding: 0 }}>

            {/* Header */}
            <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="pb-kpi-label"><i className="bi bi-bookmark me-1" />Ranked by Spending</span>
              <span style={{ fontSize: '0.78rem', color: 'var(--pb-muted)' }}>{activeTags.length} tag{activeTags.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Tag rows — max 13 visible, then scroll */}
            {filteredList.length === 0 ? (
              <div className="pb-empty" style={{ padding: '2rem' }}>
                No active tags for this period.
              </div>
            ) : (
              <div style={{ maxHeight: 'calc(13 * 4.17rem)', overflowY: 'auto' }}>
                {filteredList.map((tag, idx) => {
                  const color    = hashColor(tag.name, tag.color)
                  const isActive = selectedTag === tag.name
                  const avgTx    = tag.transactionCount > 0 ? tag.totalSpent / tag.transactionCount : 0
                  const maxSpent = filteredList[0]?.totalSpent ?? 1
                  const pct      = maxSpent > 0 ? (tag.totalSpent / maxSpent) * 100 : 0
                  return (
                    <div
                      key={tag.name}
                      onClick={() => selectTag(tag.name)}
                      style={{ display: 'grid', gridTemplateColumns: '2.2rem 1fr auto', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderBottom: '1px solid var(--pb-border)', cursor: 'pointer', background: isActive ? color + '18' : undefined, transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '' }}
                    >
                      {/* Rank badge */}
                      <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.72rem', color }}>
                        {idx + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: isActive ? 'var(--pb-primary)' : 'var(--pb-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6, flexShrink: 0, verticalAlign: 'middle' }} />
                          {tag.name}
                          {isActive && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.72rem' }} />}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{tag.transactionCount} txns &middot; {money(avgTx)}/txn</div>
                        <div style={{ marginTop: 4, height: 3, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                          <div style={{ width: Math.min(pct, 100) + '%', height: '100%', background: color, borderRadius: 4 }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{money(tag.totalSpent)}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{pct.toFixed(1)}%</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Detail panel ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ minHeight: 200 }}>

            {selectedTag == null && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: 'var(--pb-muted)' }}>
                <i className="bi bi-hand-index" style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.75rem' }} />
                <div style={{ fontSize: '0.88rem' }}>Select a tag to view details</div>
              </div>
            )}

            {selectedTag && detailTag && (
              <div>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: tagColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', color: tagColor }}>
                    <i className="bi bi-bookmark-fill" />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedTag}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: 3 }}>{detailTag.transactionCount} transactions</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.25rem' }}>{money(detailTag.totalSpent)}</div>
                  </div>
                </div>

                {/* KPI row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  {[
                    { label: 'Avg Transaction', value: detailTag.transactionCount > 0 ? money(detailTag.totalSpent / detailTag.transactionCount) : '—', icon: 'bi-calculator' },
                    { label: 'Active Months',   value: String(chartActiveMonths), icon: 'bi-calendar-check' },
                    { label: 'First Seen',       value: tagFirstLast?.first?.slice(0, 7) ?? '—', icon: 'bi-calendar-plus' },
                    { label: 'Last Activity',    value: tagFirstLast?.last?.slice(0, 7)  ?? '—', icon: 'bi-calendar-event' },
                  ].map(kpi => (
                    <div key={kpi.label} style={{ background: 'var(--pb-glass-bg)', borderRadius: 8, padding: '0.6rem 0.65rem', border: '1px solid var(--pb-border)' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--pb-muted)', marginBottom: 4 }}>
                        <i className={'bi ' + kpi.icon + ' me-1'} />{kpi.label}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>

                {/* Spending chart */}
                {chartData && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)' }}>
                        Spending over time — as of {chartRefLabel}
                        {(selectedCategory || selectedMerchant) && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>— filtered</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button onClick={() => setChartOffset(o => o - 6)} disabled={!canGoBack}    style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoBack    ? 'pointer' : 'default', color: canGoBack    ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-left"  style={{ fontSize: '0.85rem' }} /></button>
                        <button onClick={() => setChartOffset(o => o + 6)} disabled={!canGoForward} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoForward ? 'pointer' : 'default', color: canGoForward ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-right" style={{ fontSize: '0.85rem' }} /></button>
                      </div>
                    </div>
                    <div style={{ height: 150 }}><Line data={chartData} options={CHART_OPTS} /></div>
                  </div>
                )}

                {/* Categories — selectable, filters merchants + table */}
                {tagCategories.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>Categories</div>
                    {tagCategories.map(c => {
                      const isCatActive = selectedCategory === c.name
                      const cColor = hashColor(c.name)
                      return (
                        <div
                          key={c.name}
                          onClick={() => { setChartOffset(0); setSelectedCategory(prev => prev === c.name ? null : c.name); setSelectedMerchant(null) }}
                          style={{ marginBottom: 6, cursor: 'pointer', borderRadius: 6, padding: '0.35rem 0.5rem', background: isCatActive ? cColor + '18' : undefined, border: isCatActive ? '1.5px solid ' + cColor + '66' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isCatActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isCatActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: isCatActive ? 700 : 500, color: isCatActive ? 'var(--pb-primary)' : undefined }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cColor, flexShrink: 0 }} />
                              {c.name}
                              {isCatActive && <i className="bi bi-check-circle-fill" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem' }} />}
                              <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{c.count}&times;</span>
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{c.percentage.toFixed(1)}%</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{money(c.totalSpent)}</span>
                            </div>
                          </div>
                          <div style={{ height: 4, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                            <div style={{ width: Math.min(c.percentage, 100) + '%', height: '100%', background: cColor, borderRadius: 4 }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Merchants — selectable, filters table; capped at 20 with scroll */}
                {tagMerchants.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>
                      Merchants {tagMerchants.length > 20 && <span style={{ fontWeight: 400, textTransform: 'none' }}>({tagMerchants.length} total)</span>}
                    </div>
                    <div style={{ maxHeight: tagMerchants.length > 20 ? '420px' : undefined, overflowY: tagMerchants.length > 20 ? 'auto' : undefined }}>
                    {tagMerchants.slice(0, 20).map((m) => {
                      const isMerchActive = selectedMerchant === m.name
                      const mColor = hashColor(m.name)
                      return (
                        <div
                          key={m.name}
                          onClick={() => { setChartOffset(0); setSelectedMerchant(prev => prev === m.name ? null : m.name) }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.3rem 0.5rem', marginBottom: 2, borderRadius: 6, cursor: 'pointer', background: isMerchActive ? mColor + '18' : undefined, border: isMerchActive ? '1.5px solid ' + mColor + '44' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isMerchActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isMerchActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: mColor, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: isMerchActive ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.name}
                            {isMerchActive && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem' }} />}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', flexShrink: 0 }}>{m.count}&times;</span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', flexShrink: 0 }}>{m.percentage.toFixed(1)}%</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>{money(m.totalSpent)}</span>
                        </div>
                      )
                    })}
                    </div>
                  </div>
                )}

                {tagCategories.length === 0 && tagMerchants.length === 0 && (
                  <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', textAlign: 'center', padding: '1rem 0' }}>No category or merchant data for this tag.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction table */}
      <TransactionsTable
        items={sortedTxns as Transaction[]}
        showPB
        stateKey="tags"
        resetKey={`${selectedTag ?? ''}|${selectedCategory ?? ''}|${selectedMerchant ?? ''}`}
        onRowClick={goToEdit}
        headerExtra={hasFilters ? (
          <>
            {selectedTag != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-bookmark" style={{ fontSize: '0.75rem' }} />{selectedTag}
                <button onClick={() => { setSelectedTag(null); setSelectedCategory(null); setSelectedMerchant(null) }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedCategory != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-tag" style={{ fontSize: '0.75rem' }} />{selectedCategory}
                <button onClick={() => setSelectedCategory(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedMerchant != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-shop" style={{ fontSize: '0.75rem' }} />{selectedMerchant}
                <button onClick={() => setSelectedMerchant(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
          </>
        ) : undefined}
      />
    </div>
  )
}
